import React, { useState, useEffect } from 'react';
import patternsRadarService from '../../../services/patternsRadarService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faKey,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faSlidersH,
  faEnvelope,
  faServer,
} from '@fortawesome/free-solid-svg-icons';

const QuotaSettingsModal = ({ onClose, onKeyUpdated }) => {
  const [dbConfig, setDbConfig] = useState(null);
  const [healthStatus, setHealthStatus] = useState('checking'); // 'ok' | 'error' | 'checking'
  const [accountInfo, setAccountInfo] = useState(null);
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState(true);
  const [plans, setPlans] = useState(null);

  const loadAccountData = async () => {
    // 0. Fetch DB configuration status
    try {
      const cfg = await patternsRadarService.getConfig();
      setDbConfig(cfg);
    } catch (e) {
      setDbConfig(null);
    }

    // 1. Check health
    try {
      const h = await patternsRadarService.checkHealth();
      setHealthStatus(h?.status === 'ok' ? 'ok' : 'error');
    } catch (e) {
      setHealthStatus('error');
    }

    // 2. Who am I
    try {
      const acc = await patternsRadarService.whoAmI();
      setAccountInfo(acc);
    } catch (e) {
      setAccountInfo(null);
    }

    // 3. Email notifications
    try {
      const notif = await patternsRadarService.getNotificationPreferences();
      setEmailAlertsEnabled(notif?.email_alerts_enabled ?? true);
    } catch (e) {
      // Ignore if no auth
    }

    // 4. Plans
    try {
      const p = await patternsRadarService.getPlans();
      setPlans(p);
    } catch (e) {
      // Ignore
    }
  };

  useEffect(() => {
    loadAccountData();
  }, []);

  const handleToggleEmailNotifications = async (val) => {
    setEmailAlertsEnabled(val);
    try {
      await patternsRadarService.updateNotificationPreferences(val);
    } catch (e) {
      alert('Failed to update email preferences. Ensure valid API key in tbl_configuration.');
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title font-weight-bold">
              <FontAwesomeIcon icon={faSlidersH} className="text-primary me-2" />
              Street Analysis Configuration & Plan Quotas
            </h6>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body p-3">
            {/* Health Status Bar */}
            <div className="d-flex justify-content-between align-items-center p-2 mb-3 rounded border bg-light">
              <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon
                  icon={faServer}
                  className={
                    healthStatus === 'ok' ? 'text-success' : 'text-danger'
                  }
                />
                <span className="small font-weight-bold">
                  PatternsRadar Engine Status:
                </span>
                <span
                  className={`badge ${
                    healthStatus === 'ok' ? 'bg-success' : 'bg-danger'
                  }`}
                >
                  {healthStatus === 'ok'
                    ? 'ONLINE (HTTP 200 OK)'
                    : 'UNAVAILABLE / CONNECTIVITY ERROR'}
                </span>
              </div>
              <small className="text-muted">Route: Backend Proxy</small>
            </div>

            {/* Database Configuration Status */}
            <div className="card mb-3 border">
              <div className="card-body py-2">
                <h6 className="font-weight-bold small text-muted text-uppercase mb-2">
                  <FontAwesomeIcon icon={faKey} className="me-1" />
                  Database Configuration (tbl_configuration)
                </h6>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">API Key:</span>
                    <span className={`badge ${dbConfig?.has_api_key ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {dbConfig?.has_api_key ? 'Configured in Database' : 'Missing in tbl_configuration'}
                    </span>
                  </div>
                  {dbConfig?.webhook_url && (
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="small text-muted">Saved Webhook URL:</span>
                      <span className="small font-monospace text-truncate" style={{ maxWidth: '280px' }}>
                        {dbConfig.webhook_url}
                      </span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Webhook Secret:</span>
                    <span className={`badge ${dbConfig?.has_webhook_secret ? 'bg-success' : 'bg-secondary'}`}>
                      {dbConfig?.has_webhook_secret ? 'Captured & Stored in DB' : 'Not Set Yet (Auto-generated on Alert Save)'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Usage & Quotas (if auth) */}
            {accountInfo ? (
              <div className="card mb-3 border">
                <div className="card-body py-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="font-weight-bold small text-muted text-uppercase mb-0">
                      Account Tier: {accountInfo.plan?.toUpperCase()} Plan (
                      {accountInfo.plan_status})
                    </h6>
                    <span className="badge bg-primary">
                      User: {accountInfo.user?.email}
                    </span>
                  </div>

                  <div className="row g-2 text-center small mb-2">
                    <div className="col-md-3">
                      <div className="p-2 border rounded bg-light">
                        <span className="text-muted d-block">Saved Scans</span>
                        <strong>
                          {accountInfo.usage?.saved_scans || 0} /{' '}
                          {accountInfo.limits?.max_saved_scans || 100}
                        </strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="p-2 border rounded bg-light">
                        <span className="text-muted d-block">Alert Slots</span>
                        <strong>
                          {accountInfo.usage?.alert_scans || 0} /{' '}
                          {accountInfo.limits?.max_alert_scans || 50}
                        </strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="p-2 border rounded bg-light">
                        <span className="text-muted d-block">Scans / Min</span>
                        <strong>
                          {accountInfo.limits?.scan_rate_per_min || 100}
                        </strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="p-2 border rounded bg-light">
                        <span className="text-muted d-block">Webhooks</span>
                        <strong>
                          {accountInfo.limits?.webhooks_enabled
                            ? 'Enabled'
                            : 'Disabled'}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="alert alert-info py-2 small mb-3">
                No authenticated account loaded. Enter your API key above to
                load plan limits and account usage.
              </div>
            )}

            {/* Global Notification Preferences */}
            <div className="card border">
              <div className="card-body py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="font-weight-bold small d-block">
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                      Global Email Alerts (Account-wide)
                    </span>
                    <small className="text-muted">
                      Toggle whether nightly alert evaluation emails are sent to
                      your team account.
                    </small>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="globalEmailSwitch"
                      checked={emailAlertsEnabled}
                      onChange={(e) =>
                        handleToggleEmailNotifications(e.target.checked)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-default btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotaSettingsModal;
